import queryString from 'query-string'

export const deleteQueryParam = (
  key: string,
  { search = window.location.search, replace = false } = {}
): void => {
  if (!key) {
    return
  }

  const searchParams = queryString.parse(search)

  if (!searchParams[key]) {
    return
  }

  delete searchParams[key]

  const urlState = queryString.stringify(searchParams)

  if (replace) {
    window.history.replaceState(window.history.state, '', '?' + urlState)
  } else {
    window.history.pushState(window.history.state, '', '?' + urlState)
  }
}

export const getQueryParam = (
  key: string,
  search: string = window.location.search
): string | (string | null)[] | null => {
  const searchParams = queryString.parse(search)

  return searchParams[key]
}

export const getQueryParamsByPrefix = (
  prefix: string,
  search: string = window.location.search
): Record<string, string | string[] | null> => {
  const searchParams = queryString.parse(search, { arrayFormat: 'comma' })
  const result: Record<string, string | string[] | null> = {}

  for (const [key, value] of Object.entries(searchParams)) {
    if (key.startsWith(prefix)) {
      result[key] = Array.isArray(value)
        ? value.filter((v): v is string => v !== null)
        : value
    }
  }

  return result
}

export const getQueryParamId = (
  key: string,
  search: string = window.location.search
): number | (number | null)[] | null => {
  const value = getQueryParam(key, search)

  if (Array.isArray(value)) {
    return value.map((v: string | null) => (v ? parseInt(v) : null))
  }

  return value ? parseInt(value) : null
}

export const hasQueryParam = (
  key: string,
  search: string = window.location.search
): boolean => {
  const searchParams = queryString.parse(search)

  return Boolean(searchParams[key])
}

/**
 * Checks if a string is a valid URL.
 * @param url - The URL to check
 * @param allowedProtocols - Optional array of allowed protocols (e.g., ['http', 'https'])
 * @returns Whether the string is a valid URL and matches allowed protocols if specified.
 */
export const isLink = (url: string, allowedProtocols?: string[]): boolean => {
  try {
    const parsedUrl = new URL(url)

    if (allowedProtocols && allowedProtocols.length > 0) {
      return allowedProtocols.includes(parsedUrl.protocol.replace(':', ''))
    }

    return true
  } catch {
    return false
  }
}

// NOTE: Keep in sync with SfMarkdownText::ALLOWED_PROTOCOLS (lib/sf_markdown_text.rb)
const SF_ALLOWED_PROTOCOLS = ['http', 'https', 'vclink', 'm-files']
/**
 * Checks if a string is a valid URL with a Silverfin-allowed protocol.
 * @param {string} url - The URL to check
 * @returns {boolean} Whether the string is a valid URL with a Silverfin-allowed protocol
 */
export const isLinkWithSfAllowedProtocol = (url: string): boolean => {
  return isLink(url, SF_ALLOWED_PROTOCOLS)
}

export const setQueryParam = (
  key: string,
  value: string,
  { search = window.location.search, replace = false } = {}
): void => {
  const searchParams = queryString.parse(search)
  const urlState = queryString.stringify({ ...searchParams, [key]: value })

  if (replace) {
    window.history.replaceState(window.history.state, '', '?' + urlState)
  } else {
    window.history.pushState(window.history.state, '', '?' + urlState)
  }
}

export const setQueryParams = <KV extends Record<string, unknown>>(
  keyValuePairs: KV,
  { search = window.location.search, replace = false } = {}
): void => {
  const searchParams = queryString.parse(search)
  const urlState = queryString.stringify({ ...searchParams, ...keyValuePairs })

  if (replace) {
    window.history.replaceState(window.history.state, '', '?' + urlState)
  } else {
    window.history.pushState(window.history.state, '', '?' + urlState)
  }
}
